import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { AVAILABLE_USERS_EXTRA_TYPE } from '../../../../../../../consts/player';
import { HTMLEditorQuill } from '../../../../../../../components/HTMLEditorQuill/HTMLEditorQuill';
import { ScheduleSendingForm } from '../../../../../../../components/GeneralMessages/ScheduleSendingForm/ScheduleSendingForm';
import { uploadFileAllPath } from '../../../../../../../helpers/service/file';
import { AppUser } from '../../../../../../../views/App/App';
import { AvailableUserExtended } from '../SchoolEventView';
import { EventCustomNotificationPreview } from './EventCustomNotificationPreview';
import { SchoolEvent } from '../../../../../../../models/event';
import { getEventGeneratedName } from '../../../../../../../helpers/accessor/accessor';
import './EventCustomNotificationWizard.scss';
import '../../../../../../../components/GeneralMessagePreview/GeneralMessagePreview.css';

interface UserSwitch {
  name: string;
  permissionId: string;
  permissionIds: string[];
  value: boolean;
  firstName: string;
  lastName: string;
}

interface FormValues {
  sendToAll: boolean;
  sendEmail: boolean;
  sendPush: boolean;
  emailSubject: string;
  emailBody: string;
  pushTitle: string;
  pushBody: string;
  attachments: Array<{ fileName: string; fileSize: number; externalFileId: string; link: string }>;
  parents: UserSwitch[];
  players: UserSwitch[];
  staff: UserSwitch[];
  selectAllParents: boolean;
  selectAllPlayers: boolean;
  selectAllStaff: boolean;
  customizeRecipients: boolean;
  submitAction: 'SEND_NOW' | 'SCHEDULE';
  scheduleAt: string;
}

interface Props {
  user: AppUser;
  event: SchoolEvent;
  availableUsers: AvailableUserExtended[];
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

const validationSchema = Yup.object().shape({
  sendToAll: Yup.boolean(),
  sendEmail: Yup.boolean(),
  sendPush: Yup.boolean(),
  emailSubject: Yup.string().when('sendEmail', {
    is: true,
    then: schema => schema.required('Required'),
    otherwise: schema => schema.notRequired()
  }),
  emailBody: Yup.string().when('sendEmail', {
    is: true,
    then: schema => schema.required('Required'),
    otherwise: schema => schema.notRequired()
  }),
  pushTitle: Yup.string().when('sendPush', {
    is: true,
    then: schema => schema.required('Required'),
    otherwise: schema => schema.notRequired()
  }),
  pushBody: Yup.string().when('sendPush', {
    is: true,
    then: schema => schema.required('Required'),
    otherwise: schema => schema.notRequired()
  }),
  scheduleAt: Yup.string().when('submitAction', {
    is: 'SCHEDULE',
    then: schema =>
      schema
        .required('Please select date and time')
        .test('is-future', 'Scheduled time must be in the future', value => {
          if (!value) return false;
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return false;
          return date.getTime() > Date.now();
        }),
    otherwise: schema => schema.notRequired()
  })
});

const ICONS = {
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  check: 'M20 6 9 17l-5-5',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2M22 7l-10 6L2 7',
  paperclip:
    'M21.44 11.05 12 20.49a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75'
};

const Icon = ({ name, size = 16 }: { name: keyof typeof ICONS; size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={ICONS[name]} />
  </svg>
);

export const EventCustomNotificationWizard: React.FC<Props> = ({ user, event, availableUsers, onCancel, onSubmit }) => {
  const [step, setStep] = useState<'compose' | 'recipients' | 'review'>('compose');
  const [recipientFilter, setRecipientFilter] = useState<'all' | 'players' | 'parents' | 'staff'>('all');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [appliedRecipientSearch, setAppliedRecipientSearch] = useState('');
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const valuesRef = useRef<FormValues | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const finalSubmitArmedRef = useRef(false);

  useEffect(() => {
    finalSubmitArmedRef.current = false;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [step]);

  const getInitialGroup = (type: AvailableUserExtended['extra']['type']): UserSwitch[] => {
    const grouped = availableUsers
      .filter(availableUser => availableUser.extra.type === type)
      .reduce((result, availableUser) => {
        const userId = String(availableUser.userId);
        if (!result[userId]) {
          result[userId] = {
            name: userId,
            permissionId: availableUser.permissionId,
            permissionIds: [],
            value: false,
            firstName: availableUser.firstName,
            lastName: availableUser.lastName
          };
        }
        if (availableUser.permissionId && result[userId].permissionIds.indexOf(availableUser.permissionId) === -1) {
          result[userId].permissionIds.push(availableUser.permissionId);
        }
        result[userId].value = result[userId].value || availableUser.isChecked;
        return result;
      }, {} as Record<string, UserSwitch>);

    return Object.keys(grouped).map(userId => grouped[userId]);
  };

  const getSmartEventTitle = () => {
    const generatedName = getEventGeneratedName(event, { user }).trim();
    const sportName = event.sport && event.sport.name ? event.sport.name : '';
    const fallbackName = [sportName, event.startTime ? new Date(event.startTime).toLocaleDateString('en-GB') : '']
      .filter(Boolean)
      .join(' ');
    const eventName = generatedName || fallbackName || 'event';

    return `Update: ${eventName}`;
  };

  const smartEventTitle = getSmartEventTitle();

  const initialValues: FormValues = {
    sendToAll: true,
    sendEmail: true,
    sendPush: true,
    emailSubject: smartEventTitle,
    emailBody: '',
    pushTitle: smartEventTitle,
    pushBody: '',
    attachments: [],
    parents: getInitialGroup(AVAILABLE_USERS_EXTRA_TYPE.PARENT),
    players: getInitialGroup(AVAILABLE_USERS_EXTRA_TYPE.PLAYER),
    staff: getInitialGroup(AVAILABLE_USERS_EXTRA_TYPE.STAFF),
    selectAllParents: true,
    selectAllPlayers: true,
    selectAllStaff: true,
    customizeRecipients: false,
    submitAction: 'SEND_NOW',
    scheduleAt: ''
  };

  const countSelected = (values: FormValues) =>
    [...values.players, ...values.parents, ...values.staff].filter(item => item.value).length;

  const isEditorEmpty = (html: string) => !html || html === '<p><br></p>' || !html.trim();

  const hasNotificationContent = (values: FormValues | null) => {
    if (!values) {
      return false;
    }

    return (
      !!values.emailSubject.trim() ||
      !isEditorEmpty(values.emailBody) ||
      !!values.pushTitle.trim() ||
      !!values.pushBody.trim() ||
      values.attachments.length > 0 ||
      !!values.scheduleAt
    );
  };

  const requestClose = () => {
    if (!hasNotificationContent(valuesRef.current)) {
      onCancel();
      return;
    }

    setIsCloseConfirmOpen(true);
  };

  useEffect(() => {
    window.addEventListener('event-notify-close-request', requestClose);

    return () => {
      window.removeEventListener('event-notify-close-request', requestClose);
    };
  });

  const renderChannelCard = (
    id: 'sendEmail' | 'sendPush',
    title: string,
    meta: string,
    icon: keyof typeof ICONS,
    values: FormValues,
    setFieldValue
  ) => {
    const isOn = values[id];

    return (
      <button
        type="button"
        className={isOn ? 'evNotifyChannel evNotifyChannel--active' : 'evNotifyChannel'}
        onClick={() => setFieldValue(id, !isOn)}
      >
        <span className="evNotifyChannel__icon">
          <Icon name={icon} />
        </span>
        <span className="evNotifyChannel__copy">
          <span className="evNotifyChannel__title">{title}</span>
          <span className="evNotifyChannel__meta">{meta}</span>
        </span>
        <span className="evNotifyCheck">{isOn ? <Icon name="check" size={13} /> : null}</span>
      </button>
    );
  };

  const renderRecipientToggle = (label: string, count: number, value: boolean, onChange: () => void, key: string) => (
    <button
      key={key}
      type="button"
      className={
        count === 0
          ? 'evNotifyAudienceChip evNotifyAudienceChip--disabled'
          : value
          ? 'evNotifyAudienceChip evNotifyAudienceChip--active'
          : 'evNotifyAudienceChip'
      }
      disabled={count === 0}
      aria-disabled={count === 0}
      onClick={onChange}
    >
      {count > 0 && value ? <Icon name="check" size={14} /> : null}
      <span>{label}</span>
      <span className="evNotifyAudienceChip__count">{count}</span>
    </button>
  );

  const getRecipientGroups = (values: FormValues) =>
    [
      { key: 'players', label: 'Students', toggle: 'selectAllPlayers', items: values.players },
      { key: 'parents', label: 'Parents / carers', toggle: 'selectAllParents', items: values.parents },
      { key: 'staff', label: 'Staff & coach', toggle: 'selectAllStaff', items: values.staff }
    ] as const;

  const updateRecipient = (
    setFieldValue,
    groupKey: string,
    recipientKey: string,
    value: boolean,
    values: FormValues
  ) => {
    const items = values[groupKey].map((recipient: UserSwitch) =>
      recipient.name === recipientKey ? { ...recipient, value } : recipient
    );
    const toggleByGroup = {
      players: 'selectAllPlayers',
      parents: 'selectAllParents',
      staff: 'selectAllStaff'
    };

    setFieldValue(groupKey, items);
    setFieldValue(toggleByGroup[groupKey], items.length > 0 && items.every((recipient: UserSwitch) => recipient.value));
  };

  const renderRecipientRow = (
    recipient: UserSwitch,
    groupKey: string,
    groupLabel: string,
    actionLabel: string,
    onClick: () => void
  ) => {
    const initials = `${recipient.firstName.charAt(0)}${recipient.lastName.charAt(0)}`.toUpperCase();

    return (
      <button key={`${groupKey}_${recipient.name}`} type="button" className="evNotifyPickerPerson" onClick={onClick}>
        <span className="evNotifyPerson__avatar">{initials || '?'}</span>
        <span className="evNotifyPickerPerson__copy">
          <span className="evNotifyPickerPerson__name">
            {recipient.firstName} {recipient.lastName}
          </span>
          <span className="evNotifyPickerPerson__meta">{groupLabel}</span>
        </span>
        <span className="evNotifyPickerPerson__action">{actionLabel}</span>
      </button>
    );
  };

  const renderRecipientsStep = (values: FormValues, setFieldValue) => {
    const groups = getRecipientGroups(values);
    const normalizedSearch = appliedRecipientSearch.trim().toLowerCase();
    const allRecipients = groups.reduce(
      (acc, group) =>
        acc.concat(
          group.items.map(recipient => ({
            ...recipient,
            groupKey: group.key,
            groupLabel: group.label
          }))
        ),
      []
    );
    const filteredRecipients = allRecipients.filter(recipient => {
      const isFilterMatch = recipientFilter === 'all' || recipient.groupKey === recipientFilter;
      const fullName = `${recipient.firstName} ${recipient.lastName}`.toLowerCase();
      const isSearchMatch = normalizedSearch === '' || fullName.indexOf(normalizedSearch) !== -1;
      return isFilterMatch && isSearchMatch;
    });
    const availableRecipients = filteredRecipients.filter(recipient => !recipient.value);
    const selectedRecipients = filteredRecipients.filter(recipient => recipient.value);
    const setRecipientsValue = (recipients, value: boolean) => {
      const namesByGroup = recipients.reduce((acc, recipient) => {
        acc[recipient.groupKey] = acc[recipient.groupKey] || [];
        acc[recipient.groupKey].push(recipient.name);
        return acc;
      }, {});
      const toggleByGroup = {
        players: 'selectAllPlayers',
        parents: 'selectAllParents',
        staff: 'selectAllStaff'
      };

      Object.keys(namesByGroup).forEach(groupKey => {
        const names = namesByGroup[groupKey];
        const items = values[groupKey].map((recipient: UserSwitch) =>
          names.indexOf(recipient.name) !== -1 ? { ...recipient, value } : recipient
        );

        setFieldValue(groupKey, items);
        setFieldValue(
          toggleByGroup[groupKey],
          items.length > 0 && items.every((recipient: UserSwitch) => recipient.value)
        );
      });
    };

    return (
      <section className="evNotifyRecipientPicker">
        <div className="evNotifyRecipientPicker__top">
          <div>
            <div className="evNotifyPanel__title">
              <Icon name="users" />
              Choose recipients
            </div>
            <div className="evNotifyRecipientPicker__hint">
              Move people between available and selected recipients before sending.
            </div>
          </div>
        </div>

        <div className="evNotifyRecipientPicker__filters">
          <input
            className="evNotifyInput"
            value={recipientSearch}
            onChange={event => setRecipientSearch(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setAppliedRecipientSearch(recipientSearch);
              }
            }}
            placeholder="Search recipients"
          />
          <div className="evNotifyRecipientFilterChips">
            {[
              { key: 'all', label: 'All' },
              { key: 'players', label: 'Students' },
              { key: 'parents', label: 'Parents' },
              { key: 'staff', label: 'Staff' }
            ].map(filter => (
              <button
                key={filter.key}
                type="button"
                className={
                  recipientFilter === filter.key
                    ? 'evNotifyRecipientFilterChip evNotifyRecipientFilterChip--active'
                    : 'evNotifyRecipientFilterChip'
                }
                onClick={() => setRecipientFilter(filter.key as any)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="evNotifyPickerPanels">
          <div className="evNotifyPickerPanel">
            <div className="evNotifyPickerPanel__header">
              <span>Available people</span>
              <div className="evNotifyPickerPanel__actions">
                <strong>{availableRecipients.length}</strong>
                <button
                  type="button"
                  className="evNotifyPickerPanel__action"
                  disabled={availableRecipients.length === 0}
                  onClick={() => setRecipientsValue(availableRecipients, true)}
                >
                  Add all
                </button>
              </div>
            </div>
            <div className="evNotifyPickerPanel__list">
              {availableRecipients.length > 0 ? (
                availableRecipients.map(recipient =>
                  renderRecipientRow(recipient, recipient.groupKey, recipient.groupLabel, 'Add', () =>
                    updateRecipient(setFieldValue, recipient.groupKey, recipient.name, true, values)
                  )
                )
              ) : (
                <div className="evNotifyPickerEmpty">No available recipients</div>
              )}
            </div>
          </div>

          <div className="evNotifyPickerPanel">
            <div className="evNotifyPickerPanel__header">
              <span>Will be notified</span>
              <div className="evNotifyPickerPanel__actions">
                <strong>{selectedRecipients.length}</strong>
                <button
                  type="button"
                  className="evNotifyPickerPanel__action"
                  disabled={selectedRecipients.length === 0}
                  onClick={() => setRecipientsValue(selectedRecipients, false)}
                >
                  Remove all
                </button>
              </div>
            </div>
            <div className="evNotifyPickerPanel__list">
              {selectedRecipients.length > 0 ? (
                selectedRecipients.map(recipient =>
                  renderRecipientRow(recipient, recipient.groupKey, recipient.groupLabel, 'Remove', () =>
                    updateRecipient(setFieldValue, recipient.groupKey, recipient.name, false, values)
                  )
                )
              ) : (
                <div className="evNotifyPickerEmpty">No selected recipients</div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="send-notifications-wizard">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          if (step !== 'review' || !finalSubmitArmedRef.current) return;
          finalSubmitArmedRef.current = false;
          const payload: any = { ...values };
          delete payload.customizeRecipients;
          onSubmit(payload);
        }}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
          valuesRef.current = values;
          const selectedCount = countSelected(values);
          const channelCount = [values.sendEmail, values.sendPush].filter(Boolean).length;
          const isEmailBodyEmpty = isEditorEmpty(values.emailBody);
          const isPushBodyEmpty = !values.pushBody || !values.pushBody.trim();
          const isPushTitleEmpty = !values.pushTitle || !values.pushTitle.trim();
          const isEmailSubjectEmpty = !values.emailSubject || !values.emailSubject.trim();
          const isMessageStepDisabled =
            channelCount === 0 ||
            selectedCount === 0 ||
            (values.sendEmail && (isEmailSubjectEmpty || isEmailBodyEmpty)) ||
            (values.sendPush && (isPushTitleEmpty || isPushBodyEmpty));
          const isSubmitDisabled =
            isMessageStepDisabled ||
            (values.submitAction === 'SCHEDULE' &&
              (!values.scheduleAt ||
                Number.isNaN(new Date(values.scheduleAt).getTime()) ||
                new Date(values.scheduleAt).getTime() <= Date.now()));

          return (
            <Form
              className="evNotifyForm"
              onSubmitCapture={event => {
                if (step !== 'review' || !finalSubmitArmedRef.current) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
            >
              {isCloseConfirmOpen && (
                <div className="evNotifyConfirmOverlay" role="dialog" aria-modal="true">
                  <div className="evNotifyConfirm">
                    <div className="evNotifyConfirm__icon">
                      <Icon name="bell" size={20} />
                    </div>
                    <div className="evNotifyConfirm__content">
                      <h4>Discard notification?</h4>
                      <p>This notification has not been sent and will not be saved.</p>
                    </div>
                    <div className="evNotifyConfirm__actions">
                      <button
                        type="button"
                        className="evNotifyButton evNotifyButton--secondary"
                        onClick={() => setIsCloseConfirmOpen(false)}
                      >
                        Keep editing
                      </button>
                      <button type="button" className="evNotifyButton evNotifyButton--danger" onClick={onCancel}>
                        Discard
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="evNotifyScroll" ref={scrollRef}>
                <div className="evNotifySteps" aria-label="Notification progress">
                  <span className={step === 'compose' ? 'is-current' : 'is-complete'}>1 Message</span>
                  {values.customizeRecipients && (
                    <span className={step === 'recipients' ? 'is-current' : step === 'review' ? 'is-complete' : ''}>
                      2 Recipients
                    </span>
                  )}
                  <span className={step === 'review' ? 'is-current' : ''}>
                    {values.customizeRecipients ? '3' : '2'} Review &amp; delivery
                  </span>
                </div>
                {step === 'compose' ? (
                  <>
                    <div className="evNotifyGrid">
                      <section className="evNotifyPanel">
                        <div className="evNotifyPanel__title">
                          <Icon name="users" />
                          Recipients
                        </div>
                        <div className="evNotifyAudienceChips">
                          {renderRecipientToggle(
                            'Students',
                            values.players.length,
                            values.players.some(recipient => recipient.value),
                            () => {
                              const checked = !values.players.some(recipient => recipient.value);
                              setFieldValue('selectAllPlayers', checked);
                              setFieldValue(
                                'players',
                                values.players.map(recipient => ({ ...recipient, value: checked }))
                              );
                            },
                            'players'
                          )}
                          {renderRecipientToggle(
                            'Parents / carers',
                            values.parents.length,
                            values.parents.some(recipient => recipient.value),
                            () => {
                              const checked = !values.parents.some(recipient => recipient.value);
                              setFieldValue('selectAllParents', checked);
                              setFieldValue(
                                'parents',
                                values.parents.map(recipient => ({ ...recipient, value: checked }))
                              );
                            },
                            'parents'
                          )}
                          {renderRecipientToggle(
                            'Staff & coach',
                            values.staff.length,
                            values.staff.some(recipient => recipient.value),
                            () => {
                              const checked = !values.staff.some(recipient => recipient.value);
                              setFieldValue('selectAllStaff', checked);
                              setFieldValue(
                                'staff',
                                values.staff.map(recipient => ({ ...recipient, value: checked }))
                              );
                            },
                            'staff'
                          )}
                        </div>
                        <label className="evNotifyRecipientMode">
                          <input
                            type="checkbox"
                            checked={values.customizeRecipients}
                            onChange={event => {
                              const enabled = event.target.checked;
                              setFieldValue('customizeRecipients', enabled);
                              if (!enabled) {
                                (['players', 'parents', 'staff'] as const).forEach(group => {
                                  const includeGroup = values[group].some(recipient => recipient.value);
                                  setFieldValue(
                                    group,
                                    values[group].map(recipient => ({ ...recipient, value: includeGroup }))
                                  );
                                });
                              }
                            }}
                          />
                          <span className="evNotifyRecipientMode__switch" aria-hidden="true" />
                          <span className="evNotifyRecipientMode__copy">
                            <strong>Customise recipients</strong>
                            <span>
                              {values.customizeRecipients
                                ? 'Next, you will choose individual people from the selected categories.'
                                : 'The notification will be sent to everyone in the selected categories.'}
                            </span>
                          </span>
                        </label>
                      </section>

                      <section className="evNotifyPanel">
                        <div className="evNotifyPanel__title">
                          <Icon name="send" />
                          Delivery channels
                        </div>
                        <div className="evNotifyChannels">
                          {renderChannelCard(
                            'sendEmail',
                            'Email',
                            'Full formatted message',
                            'mail',
                            values,
                            setFieldValue
                          )}
                          {renderChannelCard('sendPush', 'Push', 'Enabled by default', 'bell', values, setFieldValue)}
                        </div>
                      </section>
                    </div>

                    <div className="evNotifyMessageGrid">
                      <section className={values.sendEmail ? 'evNotifyPanel' : 'evNotifyPanel evNotifyPanel--disabled'}>
                        <div className="evNotifyPanel__title">
                          <Icon name="mail" />
                          Email message
                        </div>
                        <label className="evNotifyField">
                          <span>Subject</span>
                          <Field name="emailSubject" className="evNotifyInput" disabled={!values.sendEmail} />
                        </label>
                        {touched.emailSubject && errors.emailSubject ? (
                          <div className="evNotifyError">{errors.emailSubject}</div>
                        ) : null}
                        <div className="evNotifyField">
                          <span>Body</span>
                          <div className="evNotifyEditor">
                            <HTMLEditorQuill
                              user={user}
                              value={values.emailBody}
                              onEmailBodyChange={html => setFieldValue('emailBody', html)}
                              isEnabled={values.sendEmail}
                            />
                          </div>
                        </div>
                        {touched.emailBody && errors.emailBody ? (
                          <div className="evNotifyError">{errors.emailBody}</div>
                        ) : null}
                        <div className="evNotifyUpload">
                          <label className="evNotifyUpload__button">
                            <Icon name="paperclip" />
                            <span>Attach files</span>
                            <input
                              type="file"
                              multiple
                              disabled={!values.sendEmail}
                              onChange={async event => {
                                const files = Array.from(event.currentTarget.files || []);
                                const uploaded = await Promise.all(
                                  files.map(async file => {
                                    const formData = new FormData();
                                    formData.append('file', file, file.name);
                                    const { key: externalFileId, link } = await uploadFileAllPath(user, formData);
                                    return { fileName: file.name, fileSize: file.size, externalFileId, link };
                                  })
                                );
                                setFieldValue('attachments', [...values.attachments, ...uploaded]);
                              }}
                            />
                          </label>
                          {values.attachments.length > 0 && (
                            <div className="evNotifyAttachments">
                              {values.attachments.map((attachment, index) => (
                                <span key={`${attachment.externalFileId}_${index}`}>
                                  {attachment.fileName} ({(attachment.fileSize / 1024).toFixed(1)} KB)
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </section>

                      <section className={values.sendPush ? 'evNotifyPanel' : 'evNotifyPanel evNotifyPanel--disabled'}>
                        <div className="evNotifyPanel__title">
                          <Icon name="bell" />
                          Push message
                        </div>
                        <label className="evNotifyField">
                          <span>Title</span>
                          <Field name="pushTitle" className="evNotifyInput" disabled={!values.sendPush} />
                        </label>
                        {touched.pushTitle && errors.pushTitle ? (
                          <div className="evNotifyError">{errors.pushTitle}</div>
                        ) : null}
                        <label className="evNotifyField">
                          <span>Message</span>
                          <Field
                            as="textarea"
                            name="pushBody"
                            className="evNotifyTextarea"
                            rows={7}
                            disabled={!values.sendPush}
                          />
                        </label>
                        {touched.pushBody && errors.pushBody ? (
                          <div className="evNotifyError">{errors.pushBody}</div>
                        ) : null}
                      </section>
                    </div>
                  </>
                ) : step === 'recipients' ? (
                  renderRecipientsStep(values, setFieldValue)
                ) : (
                  <div className="evNotifyReviewStep">
                    <section className="evNotifyReviewIntro">
                      <div>
                        <div className="evNotifyPanel__title">Review and send</div>
                        <p>Check the message and choose when it should be delivered.</p>
                      </div>
                    </section>
                    <section className="evNotifyPreviewPanel evNotifyPreviewPanel--open">
                      <div className="evNotifyPreviewBox">
                        <EventCustomNotificationPreview
                          messageEmailSubject={values.emailSubject}
                          messageEmailBody={values.emailBody}
                          messagePushTitle={values.pushTitle}
                          messagePushBody={values.pushBody}
                          messageAttachments={values.attachments}
                        />
                      </div>
                    </section>

                    <section className="evNotifyPanel evNotifyDelivery">
                      <div className="evNotifyPanel__title">Delivery</div>
                      <div className="evNotifySegment" role="group" aria-label="Notification delivery mode">
                        <button
                          type="button"
                          className={
                            values.submitAction === 'SEND_NOW'
                              ? 'evNotifySegment__button active'
                              : 'evNotifySegment__button'
                          }
                          onClick={() => setFieldValue('submitAction', 'SEND_NOW')}
                        >
                          Send now
                        </button>
                        <button
                          type="button"
                          className={
                            values.submitAction === 'SCHEDULE'
                              ? 'evNotifySegment__button active'
                              : 'evNotifySegment__button'
                          }
                          onClick={() => {
                            setFieldValue('submitAction', 'SCHEDULE');
                            setFieldTouched('scheduleAt', true, false);
                          }}
                        >
                          Schedule
                        </button>
                      </div>
                      {values.submitAction === 'SCHEDULE' && (
                        <div className="evNotifySchedule">
                          <ScheduleSendingForm
                            placement="top"
                            error={touched.scheduleAt && errors.scheduleAt ? String(errors.scheduleAt) : undefined}
                            onScheduleDateChange={event => {
                              setFieldValue('scheduleAt', event.target.value);
                              setFieldTouched('scheduleAt', true, false);
                            }}
                          />
                        </div>
                      )}
                    </section>
                  </div>
                )}
              </div>

              <div className="wizard-actions">
                <div className="evNotifyFooterStats">
                  <span>{selectedCount} recipients</span>
                  <span>{channelCount} channels</span>
                  {step === 'compose' && isMessageStepDisabled && (
                    <em>Complete the selected message channels to continue.</em>
                  )}
                </div>
                <button type="button" className="evNotifyButton evNotifyButton--secondary" onClick={requestClose}>
                  Cancel
                </button>
                {step !== 'compose' && (
                  <button
                    type="button"
                    className="evNotifyButton evNotifyButton--secondary"
                    onClick={() => setStep(step === 'review' && values.customizeRecipients ? 'recipients' : 'compose')}
                  >
                    Back
                  </button>
                )}
                {step === 'compose' ? (
                  <button
                    type="button"
                    className="evNotifyButton evNotifyButton--primary"
                    disabled={isMessageStepDisabled}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      setStep(values.customizeRecipients ? 'recipients' : 'review');
                    }}
                  >
                    Next
                  </button>
                ) : step === 'recipients' ? (
                  <button
                    type="button"
                    className="evNotifyButton evNotifyButton--primary"
                    disabled={selectedCount === 0}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      setStep('review');
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="evNotifyButton evNotifyButton--primary"
                    disabled={isSubmitDisabled}
                    onClick={() => {
                      finalSubmitArmedRef.current = true;
                    }}
                  >
                    <Icon name="send" />
                    {values.submitAction === 'SCHEDULE' ? 'Schedule' : 'Send'}
                  </button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
